import type {
  AdminUser,
  AdminUserActiveUpdate,
  AdminUserInviteCreate,
  AppLanguage,
  Announcement,
  AnnouncementCreate,
  AnnouncementUnreadCount,
  AnnouncementUpdate,
  AttachmentPresignRequest,
  AttachmentPresignResponse,
  AttachmentDownloadResponse,
  AuditEdit,
  CurrentUser,
  DefectTag,
  EmailVerificationConfirmResponse,
  EmailVerificationSendResponse,
  Factory,
  FactoryDetail,
  FactoryUpsert,
  HelpArticle,
  HelpArticleCreate,
  HelpArticleUpdate,
  HelpImagePresignRequest,
  HelpImagePresignResponse,
  Incident,
  IncidentAttachment,
  IncidentAttachmentCreate,
  IncidentCloseRequest,
  IncidentComment,
  IncidentTaskInternalComment,
  IncidentCreate,
  IncidentFormDefinition,
  IncidentFormUpsert,
  IncidentTranslations,
  IncidentDraft,
  IncidentFieldChange,
  IncidentRequest,
  IncidentRequestActivity,
  IncidentRequestActivityType,
  IncidentRequestActivityUpdate,
  IncidentRequestCreate,
  IncidentRequestUpdate,
  IncidentStatusHistory,
  IncidentUpdate,
  Organization,
  ProcessTimeline,
  ProductMaster,
  ProductMasterUpsert,
  ProductUnitMaster,
  ProductUnitMasterUpsert,
  ProductUnitOption,
  ProductionLotMaster,
  ProductionLotMasterUpsert,
  ServiceLocation,
  ServiceLocationDetail,
  ServiceLocationUpsert,
  TaskActivity,
  TaskActivityUpdate,
  IncidentTask,
  IncidentTaskUpdate,
  UserAssignment,
  UserAssignmentCreate,
  UserAssignmentUpdate,
  UserContributionSummary,
  UserProfileUpdate,
} from '../types/incident';
import { AuthSessionExpiredError, getIdToken, redirectToLogin } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export class ApiRequestError extends Error {
  status: number;

  constructor(status: number) {
    super(`API request failed: ${status}`);
    this.name = 'ApiRequestError';
    this.status = status;
  }
}

export class ApiNetworkError extends Error {
  constructor() {
    super('API request failed: network error');
    this.name = 'ApiNetworkError';
  }
}

const request = async <T>(path: string, init?: RequestInit, options?: { auth?: boolean }): Promise<T> => {
  const useAuth = options?.auth ?? true;
  const send = async (idToken: string | null) =>
    fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...(init?.headers || {}),
      },
    }).catch((error) => {
      if (error instanceof TypeError) {
        throw new ApiNetworkError();
      }
      throw error;
    });

  let idToken = useAuth ? await getIdToken() : null;
  let response = await send(idToken);

  if (useAuth && response.status === 401) {
    try {
      idToken = await getIdToken({ forceRefresh: true, redirectOnFailure: false });
      response = await send(idToken);
    } catch (error) {
      if (error instanceof AuthSessionExpiredError) {
        return redirectToLogin();
      }
      throw error;
    }
  }

  if (useAuth && response.status === 401) {
    return redirectToLogin();
  }

  if (!response.ok) {
    throw new ApiRequestError(response.status);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
};

export const getCurrentUser = async (): Promise<CurrentUser> =>
  request<CurrentUser>('/api/v1/me');

export const updateMyProfile = async (payload: UserProfileUpdate): Promise<CurrentUser> =>
  request<CurrentUser>('/api/v1/me/profile', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const sendEmailVerification = async (): Promise<EmailVerificationSendResponse> =>
  request<EmailVerificationSendResponse>('/api/v1/me/email-verification/send', {
    method: 'POST',
  });

export const confirmEmailVerification = async (token: string): Promise<EmailVerificationConfirmResponse> =>
  request<EmailVerificationConfirmResponse>('/api/v1/email-verification/confirm', {
    method: 'POST',
    body: JSON.stringify({ token }),
  }, { auth: false });

export const getMyContributions = async (days = 365): Promise<UserContributionSummary> =>
  request<UserContributionSummary>(`/api/v1/me/contributions?days=${days}`);

export const listAppLanguages = async (): Promise<AppLanguage[]> =>
  request<AppLanguage[]>('/api/v1/languages');

export const listAnnouncements = async (): Promise<Announcement[]> =>
  request<Announcement[]>('/api/v1/announcements');

export const getAnnouncementUnreadCount = async (): Promise<AnnouncementUnreadCount> =>
  request<AnnouncementUnreadCount>('/api/v1/announcements/unread-count');

export const markAnnouncementRead = async (announcementId: string): Promise<void> => {
  await request<void>(`/api/v1/announcements/${announcementId}/read`, { method: 'POST' });
};

export const markAllAnnouncementsRead = async (): Promise<void> => {
  await request<void>('/api/v1/announcements/read-all', { method: 'POST' });
};

export const listAdminAnnouncements = async (): Promise<Announcement[]> =>
  request<Announcement[]>('/api/v1/admin/announcements');

export const createAdminAnnouncement = async (payload: AnnouncementCreate): Promise<Announcement> =>
  request<Announcement>('/api/v1/admin/announcements', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateAdminAnnouncement = async (
  announcementId: string,
  payload: AnnouncementUpdate
): Promise<Announcement> =>
  request<Announcement>(`/api/v1/admin/announcements/${announcementId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const deleteAdminAnnouncement = async (announcementId: string): Promise<void> => {
  await request<void>(`/api/v1/admin/announcements/${announcementId}`, { method: 'DELETE' });
};

export const listHelpArticles = async (): Promise<HelpArticle[]> =>
  request<HelpArticle[]>('/api/v1/help/articles');

export const getHelpArticle = async (slug: string): Promise<HelpArticle> =>
  request<HelpArticle>(`/api/v1/help/articles/${encodeURIComponent(slug)}`);

export const listAdminHelpArticles = async (): Promise<HelpArticle[]> =>
  request<HelpArticle[]>('/api/v1/admin/help/articles');

export const createAdminHelpArticle = async (payload: HelpArticleCreate): Promise<HelpArticle> =>
  request<HelpArticle>('/api/v1/admin/help/articles', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateAdminHelpArticle = async (articleId: string, payload: HelpArticleUpdate): Promise<HelpArticle> =>
  request<HelpArticle>(`/api/v1/admin/help/articles/${articleId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const deleteAdminHelpArticle = async (articleId: string): Promise<void> => {
  await request<void>(`/api/v1/admin/help/articles/${articleId}`, { method: 'DELETE' });
};

export const createAdminHelpImagePresign = async (
  payload: HelpImagePresignRequest
): Promise<HelpImagePresignResponse> =>
  request<HelpImagePresignResponse>('/api/v1/admin/help/images/presign', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const listAdminUsers = async (): Promise<AdminUser[]> =>
  request<AdminUser[]>('/api/v1/admin/users');

export const inviteAdminUser = async (payload: AdminUserInviteCreate): Promise<AdminUser> =>
  request<AdminUser>('/api/v1/admin/users/invitations', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const resendAdminUserInvitation = async (username: string): Promise<AdminUser> =>
  request<AdminUser>(`/api/v1/admin/users/${encodeURIComponent(username)}/invitation`, {
    method: 'POST',
  });

export const updateAdminUserActive = async (
  username: string,
  payload: AdminUserActiveUpdate
): Promise<AdminUser> =>
  request<AdminUser>(`/api/v1/admin/users/${encodeURIComponent(username)}/active`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const listOrganizations = async (): Promise<Organization[]> =>
  request<Organization[]>('/api/v1/master/organizations');

export const listFactories = async (): Promise<Factory[]> =>
  request<Factory[]>('/api/v1/master/factories');

export const getFactory = async (factoryId: string): Promise<FactoryDetail> =>
  request<FactoryDetail>(`/api/v1/master/factories/${factoryId}`);

export const createAdminFactory = async (payload: FactoryUpsert): Promise<Factory> =>
  request<Factory>('/api/v1/admin/master/factories', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateAdminFactory = async (factoryId: string, payload: FactoryUpsert): Promise<Factory> =>
  request<Factory>(`/api/v1/admin/master/factories/${factoryId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const listProductUnits = async (): Promise<ProductUnitOption[]> =>
  request<ProductUnitOption[]>('/api/v1/master/product-units');

export const listAdminProducts = async (): Promise<ProductMaster[]> =>
  request<ProductMaster[]>('/api/v1/admin/master/products');

export const createAdminProduct = async (payload: ProductMasterUpsert): Promise<ProductMaster> =>
  request<ProductMaster>('/api/v1/admin/master/products', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateAdminProduct = async (productId: string, payload: ProductMasterUpsert): Promise<ProductMaster> =>
  request<ProductMaster>(`/api/v1/admin/master/products/${productId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const listAdminIncidentForms = async (): Promise<IncidentFormDefinition[]> =>
  request<IncidentFormDefinition[]>('/api/v1/admin/forms');

export const createAdminIncidentForm = async (payload: IncidentFormUpsert): Promise<IncidentFormDefinition> =>
  request<IncidentFormDefinition>('/api/v1/admin/forms', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateAdminIncidentForm = async (
  formId: string,
  payload: IncidentFormUpsert
): Promise<IncidentFormDefinition> =>
  request<IncidentFormDefinition>(`/api/v1/admin/forms/${formId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const listAdminProductionLots = async (): Promise<ProductionLotMaster[]> =>
  request<ProductionLotMaster[]>('/api/v1/admin/master/production-lots');

export const createAdminProductionLot = async (payload: ProductionLotMasterUpsert): Promise<ProductionLotMaster> =>
  request<ProductionLotMaster>('/api/v1/admin/master/production-lots', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateAdminProductionLot = async (
  lotId: string,
  payload: ProductionLotMasterUpsert
): Promise<ProductionLotMaster> =>
  request<ProductionLotMaster>(`/api/v1/admin/master/production-lots/${lotId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const listAdminProductUnits = async (): Promise<ProductUnitMaster[]> =>
  request<ProductUnitMaster[]>('/api/v1/admin/master/product-units');

export const createAdminProductUnit = async (payload: ProductUnitMasterUpsert): Promise<ProductUnitMaster> =>
  request<ProductUnitMaster>('/api/v1/admin/master/product-units', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateAdminProductUnit = async (
  unitId: string,
  payload: ProductUnitMasterUpsert
): Promise<ProductUnitMaster> =>
  request<ProductUnitMaster>(`/api/v1/admin/master/product-units/${unitId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const listServiceLocations = async (): Promise<ServiceLocation[]> =>
  request<ServiceLocation[]>('/api/v1/master/service-locations');

export const getServiceLocation = async (serviceLocationId: string): Promise<ServiceLocationDetail> =>
  request<ServiceLocationDetail>(`/api/v1/master/service-locations/${serviceLocationId}`);

export const createAdminServiceLocation = async (payload: ServiceLocationUpsert): Promise<ServiceLocation> =>
  request<ServiceLocation>('/api/v1/admin/master/service-locations', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateAdminServiceLocation = async (
  serviceLocationId: string,
  payload: ServiceLocationUpsert
): Promise<ServiceLocation> =>
  request<ServiceLocation>(`/api/v1/admin/master/service-locations/${serviceLocationId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const listDefectTags = async (): Promise<DefectTag[]> =>
  request<DefectTag[]>('/api/v1/master/defect-tags');

export const createUserAssignment = async (payload: UserAssignmentCreate): Promise<UserAssignment> =>
  request<UserAssignment>('/api/v1/me/assignments', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateUserAssignment = async (
  assignmentId: string,
  payload: UserAssignmentUpdate
): Promise<UserAssignment> =>
  request<UserAssignment>(`/api/v1/me/assignments/${assignmentId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const deleteUserAssignment = async (assignmentId: string): Promise<void> => {
  await request<void>(`/api/v1/me/assignments/${assignmentId}`, { method: 'DELETE' });
};

export const listIncidents = async (params?: {
  status?: string;
  severity?: string;
  keyword?: string;
  factory_id?: string;
  service_location_id?: string;
}): Promise<Incident[]> => {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.severity) query.set('severity', params.severity);
  if (params?.keyword) query.set('q', params.keyword);
  if (params?.factory_id) query.set('factory_id', params.factory_id);
  if (params?.service_location_id) query.set('service_location_id', params.service_location_id);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return request<Incident[]>(`/api/v1/incidents${suffix}`);
};

export const createIncident = async (payload: IncidentCreate): Promise<Incident> =>
  request<Incident>('/api/v1/incidents', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const getIncident = async (incidentId: string): Promise<Incident> =>
  request<Incident>(`/api/v1/incidents/${incidentId}`);

export const getIncidentTranslations = async (
  incidentId: string,
  targetLanguage: string
): Promise<IncidentTranslations> =>
  request<IncidentTranslations>(
    `/api/v1/incidents/${incidentId}/translations?target_language=${encodeURIComponent(targetLanguage)}`
  );

export const updateIncident = async (
  incidentId: string,
  payload: IncidentUpdate
): Promise<Incident> =>
  request<Incident>(`/api/v1/incidents/${incidentId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const setIncidentWatch = async (incidentId: string, enabled: boolean): Promise<Incident> =>
  request<Incident>(`/api/v1/incidents/${incidentId}/watch`, {
    method: enabled ? 'PUT' : 'DELETE',
  });

export const setIncidentUseful = async (incidentId: string, enabled: boolean): Promise<Incident> =>
  request<Incident>(`/api/v1/incidents/${incidentId}/useful`, {
    method: enabled ? 'PUT' : 'DELETE',
  });

export const closeIncident = async (
  incidentId: string,
  payload: IncidentCloseRequest
): Promise<Incident> =>
  request<Incident>(`/api/v1/incidents/${incidentId}/close`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const listIncidentStatusHistory = async (incidentId: string): Promise<IncidentStatusHistory[]> =>
  request<IncidentStatusHistory[]>(`/api/v1/incidents/${incidentId}/status-history`);

export const listTaskActivities = async (incidentId: string): Promise<TaskActivity[]> =>
  request<TaskActivity[]>(`/api/v1/incidents/${incidentId}/task-activities`);

export const getProcessTimeline = async (incidentId: string): Promise<ProcessTimeline> =>
  request<ProcessTimeline>(`/api/v1/incidents/${incidentId}/process-timeline`);

export const deleteTaskActivity = async (incidentId: string, activityId: string): Promise<void> => {
  await request<void>(`/api/v1/incidents/${incidentId}/task-activities/${activityId}`, {
    method: 'DELETE',
  });
};

export const updateTaskActivity = async (
  incidentId: string,
  activityId: string,
  payload: TaskActivityUpdate
): Promise<TaskActivity> =>
  request<TaskActivity>(`/api/v1/incidents/${incidentId}/task-activities/${activityId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const listTaskActivityEdits = async (incidentId: string, activityId: string): Promise<AuditEdit[]> =>
  request<AuditEdit[]>(`/api/v1/incidents/${incidentId}/task-activities/${activityId}/edits`);

export const listIncidentFieldChanges = async (incidentId: string): Promise<IncidentFieldChange[]> =>
  request<IncidentFieldChange[]>(`/api/v1/incidents/${incidentId}/field-changes`);

export const listIncidentTasks = async (incidentId: string): Promise<IncidentTask[]> =>
  request<IncidentTask[]>(`/api/v1/incidents/${incidentId}/tasks`);

export const listIncidentRequests = async (incidentId: string): Promise<IncidentRequest[]> =>
  request<IncidentRequest[]>(`/api/v1/incidents/${incidentId}/requests`);

export const createIncidentRequest = async (
  incidentId: string,
  payload: IncidentRequestCreate
): Promise<IncidentRequest> =>
  request<IncidentRequest>(`/api/v1/incidents/${incidentId}/requests`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateIncidentRequest = async (
  incidentId: string,
  requestId: string,
  payload: IncidentRequestUpdate
): Promise<IncidentRequest> =>
  request<IncidentRequest>(`/api/v1/incidents/${incidentId}/requests/${requestId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const listIncidentRequestEdits = async (incidentId: string, requestId: string): Promise<AuditEdit[]> =>
  request<AuditEdit[]>(`/api/v1/incidents/${incidentId}/requests/${requestId}/edits`);

export const addIncidentRequestActivity = async (
  incidentId: string,
  requestId: string,
  payload: { activity_type?: IncidentRequestActivityType; body: string }
): Promise<IncidentRequestActivity> =>
  request<IncidentRequestActivity>(`/api/v1/incidents/${incidentId}/requests/${requestId}/activities`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateIncidentRequestActivity = async (
  incidentId: string,
  requestId: string,
  activityId: string,
  payload: IncidentRequestActivityUpdate
): Promise<IncidentRequestActivity> =>
  request<IncidentRequestActivity>(`/api/v1/incidents/${incidentId}/requests/${requestId}/activities/${activityId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const listIncidentRequestActivityEdits = async (
  incidentId: string,
  requestId: string,
  activityId: string
): Promise<AuditEdit[]> =>
  request<AuditEdit[]>(`/api/v1/incidents/${incidentId}/requests/${requestId}/activities/${activityId}/edits`);

export const updateIncidentTask = async (
  incidentId: string,
  taskId: string,
  payload: IncidentTaskUpdate
): Promise<IncidentTask> =>
  request<IncidentTask>(`/api/v1/incidents/${incidentId}/tasks/${taskId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const listIncidentComments = async (incidentId: string): Promise<IncidentComment[]> =>
  request<IncidentComment[]>(`/api/v1/incidents/${incidentId}/comments`);

export const addIncidentComment = async (
  incidentId: string,
  body: string
): Promise<IncidentComment> =>
  request<IncidentComment>(`/api/v1/incidents/${incidentId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });

export const updateIncidentComment = async (
  incidentId: string,
  commentId: string,
  body: string
): Promise<IncidentComment> =>
  request<IncidentComment>(`/api/v1/incidents/${incidentId}/comments/${commentId}`, {
    method: 'PATCH',
    body: JSON.stringify({ body }),
  });

export const deleteIncidentComment = async (incidentId: string, commentId: string): Promise<void> => {
  await request<void>(`/api/v1/incidents/${incidentId}/comments/${commentId}`, { method: 'DELETE' });
};

export const listIncidentTaskInternalComments = async (
  incidentId: string,
  taskId: string
): Promise<IncidentTaskInternalComment[]> =>
  request<IncidentTaskInternalComment[]>(`/api/v1/incidents/${incidentId}/tasks/${taskId}/internal-comments`);

export const addIncidentTaskInternalComment = async (
  incidentId: string,
  taskId: string,
  body: string
): Promise<IncidentTaskInternalComment> =>
  request<IncidentTaskInternalComment>(`/api/v1/incidents/${incidentId}/tasks/${taskId}/internal-comments`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });

export const updateIncidentTaskInternalComment = async (
  incidentId: string,
  taskId: string,
  commentId: string,
  body: string
): Promise<IncidentTaskInternalComment> =>
  request<IncidentTaskInternalComment>(`/api/v1/incidents/${incidentId}/tasks/${taskId}/internal-comments/${commentId}`, {
    method: 'PATCH',
    body: JSON.stringify({ body }),
  });

export const deleteIncidentTaskInternalComment = async (
  incidentId: string,
  taskId: string,
  commentId: string
): Promise<void> => {
  await request<void>(`/api/v1/incidents/${incidentId}/tasks/${taskId}/internal-comments/${commentId}`, { method: 'DELETE' });
};

export const createAttachmentPresign = async (
  incidentId: string,
  payload: AttachmentPresignRequest
): Promise<AttachmentPresignResponse> =>
  request<AttachmentPresignResponse>(`/api/v1/incidents/${incidentId}/attachments/presign`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const listIncidentAttachments = async (incidentId: string): Promise<IncidentAttachment[]> =>
  request<IncidentAttachment[]>(`/api/v1/incidents/${incidentId}/attachments`);

export const createAttachmentDownload = async (
  incidentId: string,
  attachmentId: string
): Promise<AttachmentDownloadResponse> =>
  request<AttachmentDownloadResponse>(`/api/v1/incidents/${incidentId}/attachments/${attachmentId}/download`);

export const createAttachmentThumbnailDownload = async (
  incidentId: string,
  attachmentId: string
): Promise<AttachmentDownloadResponse> =>
  request<AttachmentDownloadResponse>(`/api/v1/incidents/${incidentId}/attachments/${attachmentId}/thumbnail`);

export const addIncidentAttachment = async (
  incidentId: string,
  payload: IncidentAttachmentCreate
): Promise<IncidentAttachment> =>
  request<IncidentAttachment>(`/api/v1/incidents/${incidentId}/attachments`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const deleteIncidentAttachment = async (incidentId: string, attachmentId: string): Promise<void> => {
  await request<void>(`/api/v1/incidents/${incidentId}/attachments/${attachmentId}`, { method: 'DELETE' });
};

export const createIncidentDraft = async (text: string): Promise<IncidentDraft> =>
  request<IncidentDraft>('/api/v1/ai/incident-draft', {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
